import { useState } from "react";
import AnimatedSection from "@/components/base/AnimatedSection";

const practiceAreas = [
  {
    key: "realestate",
    english: "REAL ESTATE",
    icon: "ri-building-2-line",
    tone: "primary",
    title: "부동산사건",
    summary: "재개발·재건축부터 신탁·유치권 분쟁까지, 깊이 있는 법리로 승소를 이끕니다.",
    points: [
      { icon: "ri-map-pin-2-line", text: "서울시 관내 재개발·재건축 사건 다수 관여" },
      { icon: "ri-git-repository-line", text: "부동산 신탁·유치권 분쟁 전문 수행" },
      { icon: "ri-building-line", text: "국내 메이저 건설회사 고문변호사" },
      { icon: "ri-award-line", text: "건설업협회 간담회 초청" },
    ],
    statValue: "TOP 5",
    statLabel: "국내 상위 로펌 간담회 동석",
  },
  {
    key: "criminal",
    english: "CRIMINAL",
    icon: "ri-scales-3-line",
    tone: "accent",
    title: "형사사건",
    summary: "매년 다수의 무죄 판결을 이끌어 내며, 형사 분야에서 두각을 나타내고 있습니다.",
    points: [
      { icon: "ri-eye-line", text: "경제범죄 — 치밀함과 날카로운 법리" },
      { icon: "ri-focus-3-line", text: "성범죄 — 사실관계·현장 재구성" },
      { icon: "ri-flashlight-line", text: "강력범죄 — 순발력과 대범한 승부수" },
      { icon: "ri-government-line", text: "서울남부지검 상고심의위원회 활동" },
    ],
    statValue: "4년",
    statLabel: "상고심의위원회 위원 활동",
  },
  {
    key: "drug",
    english: "DRUG CASES",
    icon: "ri-capsule-line",
    tone: "secondary",
    title: "마약사건",
    summary: "매스컴과 연예인들이 먼저 알아본 마약사건 변호 능력을 갖추고 있습니다.",
    points: [
      { icon: "ri-star-line", text: "연예인·모델·인플루언서 사건 직접 수행" },
      { icon: "ri-live-line", text: "지상파·종편 방송 인터뷰 요청 쇄도" },
      { icon: "ri-book-open-line", text: "마약류 법령 해석·자문 능력 우수" },
      { icon: "ri-flask-line", text: "식품·화장품 개발 기업 자문 다수" },
    ],
    statValue: "다수",
    statLabel: "유명인 마약사건 직접 처리",
  },
  {
    key: "juvenile",
    english: "JUVENILE",
    icon: "ri-user-heart-line",
    tone: "primary",
    title: "소년범죄",
    summary: "처벌보다 선도와 재기 기회를 우선하는 소년사건 절차를 통해 최선의 결과를 이끌어냅니다.",
    points: [
      { icon: "ri-user-smile-line", text: "소년범죄 사건 불처분 결정 다수 진행" },
      { icon: "ri-heart-line", text: "보호처분·선도 조건부 기소유예 유도" },
      { icon: "ri-parent-line", text: "금천구 아동친화도시 추진위원회 활동" },
      { icon: "ri-graduation-cap-line", text: "한신대 법학 외래교수로 후학 양성" },
    ],
    statValue: "불처분",
    statLabel: "소년사건 불처분 결정 다수 진행",
  },
  {
    key: "traffic",
    english: "TRAFFIC",
    icon: "ri-steering-2-line",
    tone: "accent",
    title: "교통사고",
    summary: "음주운전·사고후미조치·도주 등 교통사건에서 실질적 변론으로 최선의 결과를 만들어냅니다.",
    points: [
      { icon: "ri-cup-line", text: "음주운전 3회 이상 집행유예·벌금 성공" },
      { icon: "ri-run-line", text: "사고후미조치·특가도주 무죄·집행유예" },
      { icon: "ri-car-line", text: "형사처벌·민사배상 동시 대응" },
      { icon: "ri-flag-2-line", text: "운전자·피해자·보험사 측 전방위 수행" },
    ],
    statValue: "무죄",
    statLabel: "사고후미조치·도주 사건 무죄",
  },
  {
    key: "civil",
    english: "CIVIL",
    icon: "ri-file-list-3-line",
    tone: "secondary",
    title: "민사사건",
    summary: "채권·손해배상·소유권 분쟁 등 민사소송에서 금융기관과 개인의 권리를 확실히 지켜냅니다.",
    points: [
      { icon: "ri-bank-line", text: "예금보험공사·자산관리공사 등 금융기관 전담" },
      { icon: "ri-bank-card-line", text: "채권추심·손해배상 소송 다수 수행" },
      { icon: "ri-scales-2-line", text: "사해행위취소·배당이의 승소 다수" },
      { icon: "ri-home-6-line", text: "소유권 분쟁·계약 분쟁 해결" },
    ],
    statValue: "승소",
    statLabel: "민사소송 승소 사례 다수",
  },
];

const keywordPills = ["승부욕 있는 변호사", "강단 있는 변호사", "이겨줄 것 같은 변호사"];

const toneStyles: Record<string, { bubble: string; icon: string; stat: string }> = {
  primary: {
    bubble: "bg-primary-100",
    icon: "text-primary-600",
    stat: "text-primary-500",
  },
  accent: {
    bubble: "bg-accent-100",
    icon: "text-accent-700",
    stat: "text-accent-600",
  },
  secondary: {
    bubble: "bg-secondary-100",
    icon: "text-secondary-700",
    stat: "text-secondary-600",
  },
};

export default function AboutSection() {
  const [selectedArea, setSelectedArea] = useState<(typeof practiceAreas)[number] | null>(null);
  const selectedTone = selectedArea ? toneStyles[selectedArea.tone] : null;

  const handleCardClick = (area: (typeof practiceAreas)[number]) => {
    if (window.innerWidth < 768) {
      setSelectedArea(area);
    }
  };

  return (
    <>
      <AnimatedSection>
        <section className="hidden md:block py-16 md:py-24 bg-background-100">
          <div className="w-full px-4 md:px-8 lg:px-16 max-w-[1400px] mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <p className="text-accent-500 text-sm font-medium tracking-widest mb-2">PRACTICE AREAS</p>
              <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950">
                <span className="text-primary-500">전문 분야</span>
              </h2>
              <p className="mt-4 text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                다양한 사건 수행 경험으로 쌓아온 깊이 있는 노하우를 분야별로 확인해 보세요.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {practiceAreas.map((area) => {
                const tone = toneStyles[area.tone];
                return (
                  <AnimatedSection key={area.key} delay={practiceAreas.indexOf(area) * 100}>
                    <article
                      onClick={() => handleCardClick(area)}
                      className="bg-background-50 rounded-xl border border-background-200/60 p-6 md:p-7 h-full flex flex-col hover:border-background-300/80 transition-colors cursor-pointer md:cursor-default"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className={`w-12 h-12 rounded-xl ${tone.bubble} flex items-center justify-center shrink-0`}>
                          <i className={`${area.icon} ${tone.icon} text-xl`} />
                        </div>
                        <div>
                          <h3 className="font-display text-lg md:text-xl font-bold text-foreground-950">
                            {area.title}
                          </h3>
                          <p className="text-[11px] tracking-widest text-foreground-400 mt-0.5">{area.english}</p>
                        </div>
                      </div>

                      <p className="text-sm text-foreground-700 leading-relaxed mb-3">{area.summary}</p>

                      <div className="border-t border-background-200/60 mt-1 mb-4" />

                      <ul className="hidden md:block space-y-3">
                        {area.points.map((point, idx) => (
                          <li key={idx} className="flex items-start gap-2.5">
                            <span className="w-5 h-5 rounded-md bg-background-100 border border-background-200 flex items-center justify-center shrink-0 mt-0.5">
                              <i className={`${point.icon} ${tone.icon} text-xs`} />
                            </span>
                            <span className="text-sm text-foreground-700 leading-relaxed">{point.text}</span>
                          </li>
                        ))}
                      </ul>

                      <div className="md:hidden mt-4 flex items-center justify-center gap-1.5 text-xs text-foreground-500 font-medium">
                        <span>자세히 보기</span>
                        <i className="ri-arrow-right-line text-sm" />
                      </div>
                    </article>
                  </AnimatedSection>
                );
              })}
            </div>
          </div>
        </section>
      </AnimatedSection>

      <AnimatedSection>
        <section className="md:hidden py-12 bg-background-100">
          <div className="w-full px-4 max-w-[1400px] mx-auto">
            <div className="text-center mb-8">
              <p className="text-accent-500 text-sm font-medium tracking-widest mb-2">PRACTICE AREAS</p>
              <h2 className="font-display text-2xl font-bold text-foreground-950">
                <span className="text-primary-500">전문 분야</span>
              </h2>
              <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                분야를 탭하면 자세한 내용을 확인할 수 있어요.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {practiceAreas.map((area) => {
                const tone = toneStyles[area.tone];
                return (
                  <button
                    key={area.key}
                    type="button"
                    onClick={() => setSelectedArea(area)}
                    className="w-full flex flex-col items-center gap-2.5 bg-background-50 rounded-xl border border-background-200/60 p-4 active:scale-[0.97] transition-transform cursor-pointer"
                  >
                    <div className={`w-12 h-12 rounded-xl ${tone.bubble} flex items-center justify-center shrink-0`}>
                      <i className={`${area.icon} ${tone.icon} text-xl`} />
                    </div>
                    <div className="text-center">
                      <p className="font-display text-sm font-bold text-foreground-950 whitespace-nowrap">
                        {area.title}
                      </p>
                      <p className="text-[11px] text-foreground-400 tracking-wide mt-0.5 whitespace-nowrap">
                        {area.english}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      </AnimatedSection>

      <AnimatedSection>
        <section className="py-16 md:py-24 bg-primary-900 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent-500 rounded-full blur-[150px]" />
            <div className="absolute top-0 left-0 w-72 h-72 bg-primary-500 rounded-full blur-[120px]" />
          </div>
          <div className="relative z-10 w-full px-4 md:px-8 lg:px-16 max-w-[900px] mx-auto text-center">
            <div className="w-16 h-16 rounded-full bg-accent-500/20 flex items-center justify-center mx-auto mb-6">
              <i className="ri-double-quotes-l text-accent-400 text-2xl" />
            </div>
            <h3 className="font-display text-xl md:text-2xl font-bold text-white mb-5">
              이승환 변호사의 다짐
            </h3>
            <p className="text-white/70 text-sm md:text-base leading-relaxed max-w-2xl mx-auto mb-8">
              현실에 안주하지 않고 계속 연구하고 발로 뛰며, 어려움에 처한 의뢰인들에게 희망의 사다리가 될 수 있도록
              최선을 다하겠습니다.
            </p>

            <div className="flex flex-col md:flex-row md:flex-wrap items-center justify-center gap-3">
              {keywordPills.map((pill) => (
                <span
                  key={pill}
                  className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-white/10 border border-white/15 text-white text-sm whitespace-nowrap"
                >
                  <i className="ri-double-quotes-r text-accent-400 text-sm" />
                  {pill}
                </span>
              ))}
            </div>
          </div>
        </section>
      </AnimatedSection>

      {selectedArea && selectedTone && (
        <div
          className="fixed inset-0 z-[100] md:hidden"
          onClick={() => setSelectedArea(null)}
          role="dialog"
          aria-modal="true"
        >
          <div className="absolute inset-0 bg-foreground-950/60 backdrop-blur-sm" />
          <div className="absolute inset-0 flex items-center justify-center p-5">
            <div
              className="relative w-full max-w-sm bg-background-50 rounded-2xl p-6 max-h-[80dvh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-12 h-12 rounded-xl ${selectedTone.bubble} flex items-center justify-center shrink-0`}>
                  <i className={`${selectedArea.icon} ${selectedTone.icon} text-xl`} />
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold text-foreground-950">{selectedArea.title}</h3>
                  <p className="text-[11px] tracking-widest text-foreground-400 mt-0.5">{selectedArea.english}</p>
                </div>
              </div>
              <p className="text-sm text-foreground-700 leading-relaxed mb-4">{selectedArea.summary}</p>
              <div className="border-t border-background-200/60 mb-4" />
              <ul className="space-y-3">
                {selectedArea.points.map((point, idx) => (
                  <li key={idx} className="flex items-start gap-2.5">
                    <span className="w-5 h-5 rounded-md bg-background-100 border border-background-200 flex items-center justify-center shrink-0 mt-0.5">
                      <i className={`${point.icon} ${selectedTone.icon} text-xs`} />
                    </span>
                    <span className="text-sm text-foreground-700 leading-relaxed">{point.text}</span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => setSelectedArea(null)}
                className="mt-6 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-md whitespace-nowrap"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}